/**
 * Typed API client for FrameIt backend
 * Handles authentication, error mapping, and typed responses.
 */

import type {
  ApiResponse,
  PaginatedResponse,
  ProjectListItem,
  CreateProjectRequest,
  UpdateProjectRequest,
  UploadArtworkRequest,
  UploadArtworkResponse,
  ConfirmUploadRequest,
  UserProfileResponse,
  Project,
  ArtworkRef,
  GalleryShare,
  CreateShareRequest,
  PublicViewerData,
} from '@frameit/types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const isDevMode = !import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/** Token getter function — set by the auth provider */
let tokenGetter: (() => Promise<string | null>) | null = null;

/** Set the auth token getter (called from ClerkProvider context) */
export function setTokenGetter(getter: () => Promise<string | null>): void {
  tokenGetter = getter;
}

/** API error with status and code */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Core fetch wrapper with auth and error handling */
async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);

  // Add auth token if available
  if (!isDevMode && tokenGetter) {
    const token = await tokenGetter();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  } else if (isDevMode) {
    // Dev mode — use mock auth header
    headers.set('X-Dev-User', 'dev_user');
  }

  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  const url = `${API_URL}/api${path}`;

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    let errorCode: string | undefined;

    try {
      const errorBody = (await response.json()) as ApiResponse<unknown>;
      errorMessage = errorBody.error ?? errorMessage;
      errorCode = errorBody.code;
    } catch {
      // Use status text if JSON parsing fails
      errorMessage = response.statusText || errorMessage;
    }

    throw new ApiError(errorMessage, response.status, errorCode);
  }

  return response.json() as Promise<T>;
}

// ============ Projects ============

/** List user's projects */
export async function listProjects(
  page: number = 1,
  pageSize: number = 20,
): Promise<PaginatedResponse<ProjectListItem>> {
  const res = await apiFetch<ApiResponse<PaginatedResponse<ProjectListItem>>>(
    `/projects?page=${page}&pageSize=${pageSize}`,
  );
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to list projects', 400);
  return res.data;
}

/** Get a single project by ID */
export async function getProject(projectId: string): Promise<Project> {
  const res = await apiFetch<ApiResponse<Project>>(`/projects/${projectId}`);
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Project not found', 404);
  return res.data;
}

/** Create a new project */
export async function createProject(req: CreateProjectRequest): Promise<Project> {
  const res = await apiFetch<ApiResponse<Project>>('/projects', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to create project', 400);
  return res.data;
}

/** Update a project */
export async function updateProject(
  projectId: string,
  req: UpdateProjectRequest,
): Promise<Project> {
  const res = await apiFetch<ApiResponse<Project>>(`/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to update project', 400);
  return res.data;
}

/** Delete a project */
export async function deleteProject(projectId: string): Promise<void> {
  const res = await apiFetch<ApiResponse<void>>(`/projects/${projectId}`, {
    method: 'DELETE',
  });
  if (!res.success) throw new ApiError(res.error ?? 'Failed to delete project', 400);
}

// ============ Artworks ============

/** Request an upload URL for a new artwork */
export async function requestArtworkUpload(
  req: UploadArtworkRequest,
): Promise<UploadArtworkResponse> {
  const res = await apiFetch<ApiResponse<UploadArtworkResponse>>('/artworks/upload', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to request upload', 400);
  return res.data;
}

/** Upload artwork file directly to the API */
export async function uploadArtworkDirect(artworkId: string, file: File): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);

  await apiFetch<ApiResponse<void>>(`/artworks/${artworkId}/upload-direct`, {
    method: 'PUT',
    body: formData,
  });
}

/** Confirm artwork upload with dimensions */
export async function confirmArtworkUpload(
  artworkId: string,
  req: ConfirmUploadRequest,
): Promise<ArtworkRef> {
  const res = await apiFetch<ApiResponse<ArtworkRef>>(`/artworks/${artworkId}/confirm`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to confirm upload', 400);
  return res.data;
}

/** Get artwork image URL */
export function getArtworkImageUrl(
  artworkId: string,
  variant: 'original' | 'processed' | 'thumbnail' = 'original',
): string {
  return `${API_URL}/api/artworks/${artworkId}/image?variant=${variant}`;
}

/** Delete an artwork */
export async function deleteArtwork(artworkId: string): Promise<void> {
  const res = await apiFetch<ApiResponse<void>>(`/artworks/${artworkId}`, {
    method: 'DELETE',
  });
  if (!res.success) throw new ApiError(res.error ?? 'Failed to delete artwork', 400);
}

// ============ User ============

/** Get user profile with usage stats */
export async function getUserProfile(): Promise<UserProfileResponse> {
  const res = await apiFetch<ApiResponse<UserProfileResponse>>('/user/profile');
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to get profile', 400);
  return res.data;
}

// ============ Gallery Shares ============

/** Create a share link for a project */
export async function createShare(
  projectId: string,
  req: CreateShareRequest = {},
): Promise<GalleryShare> {
  const res = await apiFetch<ApiResponse<GalleryShare>>(`/projects/${projectId}/shares`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to create share', 400);
  return res.data;
}

/** List share links for a project */
export async function listShares(projectId: string): Promise<GalleryShare[]> {
  const res = await apiFetch<ApiResponse<GalleryShare[]>>(`/projects/${projectId}/shares`);
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to list shares', 400);
  return res.data;
}

/** Revoke a share link */
export async function revokeShare(projectId: string, shareId: string): Promise<void> {
  const res = await apiFetch<ApiResponse<void>>(`/projects/${projectId}/shares/${shareId}`, {
    method: 'DELETE',
  });
  if (!res.success) throw new ApiError(res.error ?? 'Failed to revoke share', 400);
}

/** Get public viewer data (no auth required) */
export async function getPublicViewer(token: string): Promise<PublicViewerData> {
  const url = `${API_URL}/api/public/projects/${token}/viewer`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new ApiError('Share link not found', response.status);
  }
  const res = (await response.json()) as ApiResponse<PublicViewerData>;
  if (!res.success || !res.data) throw new ApiError(res.error ?? 'Failed to load viewer', 400);
  return res.data;
}

/** Get the embed viewer URL for a share token */
export function getEmbedUrl(token: string): string {
  const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  return `${baseUrl}/embed/${token}`;
}

// ============ Upload Helper ============

/**
 * Full upload flow: request → upload → confirm
 * Returns the artwork ID and dimensions.
 */
export async function uploadArtwork(
  file: File,
): Promise<{ artworkId: string; width: number; height: number }> {
  // Step 1: Request upload
  const upload = await requestArtworkUpload({
    filename: file.name,
    contentType: file.type,
    fileSize: file.size,
  });

  // Step 2: Upload file directly
  await uploadArtworkDirect(upload.artworkId, file);

  // Step 3: Get image dimensions
  const dimensions = await getImageDimensions(file);

  // Step 4: Confirm upload
  await confirmArtworkUpload(upload.artworkId, { dimensions });

  return {
    artworkId: upload.artworkId,
    width: dimensions.width,
    height: dimensions.height,
  };
}

/** Read image dimensions from a File */
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to read image dimensions'));
    };
    img.src = url;
  });
}
